import axios from 'axios';
import { getAccessToken } from '../auth/tokenStore';
import type {
  ColoringPage,
  ColoringPageListItem,
  UploadResponse,
  PaginatedResponse,
  ImageFilter,
  LineArtOptions,
  CleanupOptions,
  PreviewRequest,
  Tag,
  CreateTagRequest,
  UpdateTagRequest,
  Collection,
  CollectionDetail,
  CreateCollectionRequest,
  UpdateCollectionRequest,
} from '../types';

const api = axios.create({
  baseURL: '/api',
  // No global Content-Type: axios sets 'application/json' for object bodies and
  // 'multipart/form-data' (with the required boundary) for FormData on its own.
  // A hard-coded JSON default leaks onto file uploads, so the backend can't parse
  // the form ("No files provided").
});

// Attach the LeTech ID access token (if signed in) to every request so the
// backend can authorize it. Unauthenticated requests still go through while
// endpoints remain open.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Images API
export const imagesApi = {
  upload: async (files: File[]): Promise<UploadResponse[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    // Don't set Content-Type manually — the browser must add the multipart
    // boundary itself, otherwise ASP.NET rejects the form with a 400
    // ("Missing content-type boundary").
    const response = await api.post<UploadResponse[]>('/images/upload', formData);
    return response.data;
  },

  getAll: async (filter?: ImageFilter): Promise<PaginatedResponse<ColoringPageListItem>> => {
    const params = new URLSearchParams();
    if (filter?.page) params.append('page', String(filter.page));
    if (filter?.pageSize) params.append('pageSize', String(filter.pageSize));
    if (filter?.search) params.append('search', filter.search);
    if (filter?.tagIds?.length) filter.tagIds.forEach((id) => params.append('tagIds', String(id)));
    if (filter?.sourceType) params.append('sourceType', filter.sourceType);
    if (filter?.sortBy) params.append('sortBy', filter.sortBy);
    if (filter?.sortDescending !== undefined) params.append('sortDescending', String(filter.sortDescending));

    const response = await api.get<PaginatedResponse<ColoringPageListItem>>(`/images?${params}`);
    return response.data;
  },

  getById: async (id: number): Promise<ColoringPage> => {
    const response = await api.get<ColoringPage>(`/images/${id}`);
    return response.data;
  },

  update: async (id: number, data: { title?: string; description?: string; tagIds?: number[] }): Promise<void> => {
    await api.put(`/images/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/images/${id}`);
  },

  getFileUrl: (id: number): string => `/api/images/${id}/file`,
  getThumbnailUrl: (id: number): string => `/api/images/${id}/thumbnail`,
};

// Processing API
export const processingApi = {
  convertToLineArt: async (options: LineArtOptions): Promise<UploadResponse> => {
    const response = await api.post<UploadResponse>('/processing/to-lineart', options);
    return response.data;
  },

  cleanup: async (options: CleanupOptions): Promise<UploadResponse> => {
    const response = await api.post<UploadResponse>('/processing/cleanup', options);
    return response.data;
  },

  removeWatermark: async (
    imageId: number,
    mask: File,
    options?: {
      inpaintRadius?: number;
      maskDilation?: number;
      method?: 'Telea' | 'NavierStokes';
      title?: string;
      tagIds?: number[];
    }
  ): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('imageId', String(imageId));
    formData.append('mask', mask);
    if (options?.inpaintRadius) formData.append('inpaintRadius', String(options.inpaintRadius));
    if (options?.maskDilation) formData.append('maskDilation', String(options.maskDilation));
    if (options?.method) formData.append('method', options.method);
    if (options?.title) formData.append('title', options.title);
    if (options?.tagIds?.length) formData.append('tagIds', options.tagIds.join(','));

    // Let the browser set multipart/form-data + boundary (see upload note above).
    const response = await api.post<UploadResponse>('/processing/remove-watermark', formData);
    return response.data;
  },

  preview: async (request: PreviewRequest): Promise<Blob> => {
    const response = await api.post('/processing/preview', request, {
      responseType: 'blob',
    });
    return response.data;
  },

  toSvg: async (imageId: number, options?: { width?: number; height?: number }): Promise<string> => {
    const params = new URLSearchParams();
    if (options?.width) params.append('width', String(options.width));
    if (options?.height) params.append('height', String(options.height));
    const response = await api.get<string>(`/processing/${imageId}/svg?${params}`);
    return response.data;
  },

  downloadSvg: async (imageId: number, filename: string, options?: { width?: number; height?: number }): Promise<void> => {
    const svg = await processingApi.toSvg(imageId, options);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.replace(/\.[^.]+$/, '.svg');
    a.click();
    URL.revokeObjectURL(url);
  },

  // Render a PNG at a chosen size from the vector master (paper preset or custom px).
  downloadPng: async (
    imageId: number,
    filename: string,
    opts: { paper?: string; width?: number; height?: number; dpi?: number }
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (opts.paper) params.append('paper', opts.paper);
    if (opts.width) params.append('width', String(opts.width));
    if (opts.height) params.append('height', String(opts.height));
    if (opts.dpi) params.append('dpi', String(opts.dpi));
    const response = await api.get(`/processing/${imageId}/png?${params}`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.replace(/\.[^.]+$/, '.png');
    a.click();
    URL.revokeObjectURL(url);
  },
};

// Tags API
export const tagsApi = {
  getAll: async (): Promise<Tag[]> => {
    const response = await api.get<Tag[]>('/tags');
    return response.data;
  },

  getById: async (id: number): Promise<Tag> => {
    const response = await api.get<Tag>(`/tags/${id}`);
    return response.data;
  },

  create: async (data: CreateTagRequest): Promise<Tag> => {
    const response = await api.post<Tag>('/tags', data);
    return response.data;
  },

  update: async (id: number, data: UpdateTagRequest): Promise<void> => {
    await api.put(`/tags/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/tags/${id}`);
  },
};

// Collections API
export const collectionsApi = {
  getAll: async (): Promise<Collection[]> => {
    const response = await api.get<Collection[]>('/collections');
    return response.data;
  },

  getById: async (id: number): Promise<CollectionDetail> => {
    const response = await api.get<CollectionDetail>(`/collections/${id}`);
    return response.data;
  },

  create: async (data: CreateCollectionRequest): Promise<Collection> => {
    const response = await api.post<Collection>('/collections', data);
    return response.data;
  },

  update: async (id: number, data: UpdateCollectionRequest): Promise<void> => {
    await api.put(`/collections/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/collections/${id}`);
  },

  addImages: async (id: number, imageIds: number[]): Promise<void> => {
    await api.post(`/collections/${id}/images`, { imageIds });
  },

  removeImage: async (collectionId: number, imageId: number): Promise<void> => {
    await api.delete(`/collections/${collectionId}/images/${imageId}`);
  },

  reorder: async (id: number, imageIds: number[]): Promise<void> => {
    await api.put(`/collections/${id}/reorder`, { imageIds });
  },
};

export default api;
